/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable react/prop-types */

import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import { Link } from 'react-router';
import Icon from 'components/Icon';
import { getCookie, removeCookie, setCookie } from 'helpers/Storage';
import helperMarkdownToComponent from 'helpers/Data';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { isContentAuthorized } from 'components/AuthClientless/AuthHelper';
import { isTimestampExpired } from 'helpers/auth.clientless';
import { forceHttps, getNetworkLogoUrl, isMvpdSubscription, LINK_REL } from 'helpers/ContentHelper';
import { removeMVPDCookie, getMVPDCookie } from 'index/AuthHelper';

import {
  BUILD_VERSION,
  callTealiumEvent,
  getPreviewPassAnalyticsEvents,
  getSegmentAuthTrackEvents,
  getSegmentMVPDEvents,
  getTealiumSourceAuth,
  setSegmentPageStartSource,
  setTealiumProfileId,
  setTealiumProfileStartSource,
  setTealiumSourceAuth,
  TEALIUM_EVENT_PREFIX,
  TealiumOnboardingActions,
  TrackEvents,
} from '@foxcorp/delta-web-analytics';
import { helperGetApiRouteById, helperGetApiHeaders, apiErrorHandler } from 'helpers/ApiHelper';
import {
  getAuthNetworksConfig,
  getPreviewPassImage,
  previewPassEnabled,
  isMVPDSSOEnabled,
} from 'helpers/ConfigHelper';
import {
  helperGetHomeUrl,
  goToExternalSignIn,
  redirectToExternalUrl,
  refreshPage,
} from 'helpers/RouteHelper';
import { isPreviewPass } from 'helpers/PlayerHelper';
import classNames from 'classnames';
import Loader from 'components/Loader';
import NetworkImage from 'components/NetworkImage';
import NetworkLink from 'components/NetworkLink';
import UpsellProgressBar from 'components/UpsellProgressBar';
import MVPDStartProviders from 'components/Auth/MVPDStart/Providers';
import MvpdProviders from 'components/Auth/MvpdProviders';
import GeolocationError from 'components/Auth/PlaybackError/Geolocation';
import {
  ATTEMPTED_AUTH,
  SUCCESS_AUTH,
  SIGN_OUT_SUCCESS_AUTH,
  SIGN_OUT_ATTEMPTED_AUTH,
  userAttributeEvents as optimizelyUserAttributeEvents,
} from './OptimizelyHelper';

const PROVIDER_COMCAST = 'Comcast_SSO';
export const SUCCESS_COOKIE_NAME = 'mvpd-success';
const PREVIEW_PASS_COOKIE_NAME = 'pp-auth-token';
export const MVPD_COOKIE_NAME = 'mvpd-auth';
export const BUYTHROUGH_DATA_COOKIE_NAME = 'buythrough-scenario-data';
export const MVPD_MANUAL_LOGOUT = 'mvpd-manual-logout';
export const PREVIEW_PASS_60 = 'TempPass_fbcfox_60min';
export const PREVIEW_PASS_5 = 'TempPass_fbcfox_5min';
export const LOCKED_CONTENT_SOURCE = 'Locked Content';
const GOOGLE_PAYMENT_METHOD = 'Google Wallet';
const ROKU_PAYMENT_METHOD = 'Roku Payment';
const ON_HOLD_SUBSCRIPTION = 'Hold';
const ON_HOLD_ROKU_SUBSCRIPTION = 'HoldOnNpmt';

export const adobeAuthOptions = {
  callSetConfig: true,
  backgroundLogin: true,
  backgroundLogout: true,
};
const isLive = _.includes(_.get(window, 'location.pathname', ''), '/live');
const helperGetPerks = () => _.get(window.ExternalConfig, 'auth.perks', []);
const helperGetProfilePerks = () => _.get(window.ExternalConfig, 'profile.resumeUpsell', []);
export function getAuthPlayerLinks() {
  return _.get(window.ExternalConfig, 'auth.playerLinks', {});
}
export function generateLinkTag(theme, linkObj, onClick = () => {}) {
  const { url, text } = linkObj;
  if (!url || !text) return null;
  return (
    <Link className={theme.link} target="_blank" rel={LINK_REL} onClick={onClick} to={url}>
      {text}
    </Link>
  );
}
const getNetworkLogo = ({ theme, onClick, key }) => (
  <Link className={theme.logo} to="/" onClick={onClick}>
    <NetworkImage network={key} />
  </Link>
);
getNetworkLogo.propTypes = {
  theme: PropTypes.shape({}).isRequired,
  onClick: PropTypes.func,
  key: PropTypes.string,
};
getNetworkLogo.defaultProps = {
  onClick: () => {},
  key: '',
};

export const getPreviewPassLengthFromName = mvpd => {
  const previewPasses = [PREVIEW_PASS_5, PREVIEW_PASS_60];
  if (_.includes(previewPasses, mvpd)) {
    const duration = _.compact(_.split(mvpd, 'TempPass_fbcfox_')).pop();
    switch (duration) {
      case '60min':
        return '60 min';
      case '5min':
        return '5 min';
      default:
        break;
    }
  }
  return null;
};

export const checkAuthStartSource = (previewPassSignIn, previewPassExpired) => {
  if (previewPassSignIn) {
    if (previewPassExpired) {
      return 'preview pass:expiration';
    }
    return 'preview pass:activation';
  }
  return 'none';
};

export function getProfileSignInProps({ theme }) {
  const {
    profileResumeUpsell_signInButton: signInButtonText,
    profileResumeUpsell_signUpButton: signUpButtonText,
    profileResumeUpsell_restart: restartButtonText,
    profileResumeUpsell_benefits: moreBenefitsText,
  } = window.Language;
  const perks = helperGetProfilePerks();
  if (!perks || perks.length === 0) return null;
  const { icon: headerIcon, text: headerText } = perks[0];
  const perksHeader = (
    <div className={theme.perkHeaderContainer} key={`perk-${headerText}`}>
      <div className={theme.perksHeader}>
        <img className={theme.perkIcon} src={headerIcon} alt="Fox News logo" />
      </div>
      <div className={theme.headerPerkText}>{headerText}</div>
    </div>
  );

  const profilePerks = [];
  for (let index = 1; index < perks.length; index += 1) {
    const { icon, text } = perks[index];
    profilePerks.push(
      <div className={theme.perkContainer} key={`perk-separator-container-${text}`}>
        <div className={theme.perkInnerContainer} key={`perk-${text}`}>
          <div className={theme.perkImageContainer}>
            <img className={theme.perkIcon} src={icon} alt="Fox News logo" />
          </div>
          <div className={theme.perkText}>{text}</div>
        </div>
      </div>,
    );

    const separator =
      perks.length > index && index !== 0 && perks.length !== index + 1 ? (
        <div key={`separator-${text}`} className={theme.perksSeparator}>
          +
        </div>
      ) : null;
    profilePerks.push(separator);
  }

  return {
    signInButtonText,
    signUpButtonText,
    restartButtonText,
    moreBenefitsText,
    profileSignInTheme: theme,
    profilePerks,
    perksHeader,
  };
}

export function getSignInProps({ theme, account, previewPassExpired, previewPassSignIn }) {
  const {
    auth_selectProviderMessage: selectProviderText,
    auth_signInHeader: signInWithProviderText,
  } = window.Language;
  const signInText = <div className={theme.signInText}>{selectProviderText}</div>;
  const signInButtonText = signInWithProviderText;
  const signInInvertContent = true;
  const profileId = _.get(account, 'profileId');
  const perks = helperGetPerks();

  const signInPerks = (
    <div className={theme.perksContainer}>
      {perks.map((perk, index) => {
        const { icon, text } = perk;
        const separator =
          perks.length > index && perks.length !== index + 1 ? (
            <div key={`separator-${text}`} className={theme.perksSeparator}>
              +
            </div>
          ) : null;

        return (
          <div className={theme.perkContainer} key={`perk-separator-container-${text}`}>
            <div className={theme.perkInnerContainer} key={`perk-${text}`}>
              <div className={theme.perkImageContainer}>
                <img className={theme.perkIcon} src={icon} alt="Fox News logo" />
              </div>
              <div className={theme.perkText}>{text}</div>
            </div>
            {separator}
          </div>
        );
      })}
    </div>
  );
  const signInOnButtonClick = () => {
    const action = 'authentication_start';
    const authStartSource = getTealiumSourceAuth() || LOCKED_CONTENT_SOURCE;
    const onboardSrc = checkAuthStartSource(previewPassSignIn, previewPassExpired);
    setTealiumProfileStartSource('other');
    const onboardingStartSource = onboardSrc;
    const data = setTealiumProfileId(profileId, {
      auth_start_source: authStartSource,
      profile_start_source: undefined,
      onboarding_start_source: onboardingStartSource,
      onboarding_action: TealiumOnboardingActions.COMPLETE,
      action,
      platform_version: BUILD_VERSION,
      dcg_profile_id: profileId,
      mvpd: 'no mvpd',
      ...getCommonTealiumProps(),
    });
    callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
  };

  const signInOnCloseClick = () => {
    const action = 'authentication_start';
    const authStartSource = getTealiumSourceAuth() || LOCKED_CONTENT_SOURCE;
    const onboardSrc = checkAuthStartSource(previewPassSignIn, previewPassExpired);
    setTealiumProfileStartSource('other');
    const onboardingStartSource = onboardSrc;
    const data = setTealiumProfileId(profileId, {
      action,
      auth_start_source: authStartSource,
      dcg_profile_id: profileId,
      mvpd: 'no mvpd',
      onboarding_action: TealiumOnboardingActions.SKIP,
      onboarding_start_source: onboardingStartSource,
      platform_version: BUILD_VERSION,
      profile_start_source: undefined,
      ...getCommonTealiumProps(),
    });
    callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
  };

  return {
    signInInvertContent,
    signInText,
    signInButtonText,
    signInTheme: theme,
    signInPerks,
    signInOnButtonClick,
    signInOnCloseClick,
  };
}

export function getUnlockUpsellProps({ Style }) {
  const {
    auth_unlockUpsellText: unlockUpsellText = '',
    global_startNowButton: unlockUpsellInButtonText = '',
  } = window.Language;

  const perks = helperGetPerks();
  const unlockUpsellPerks = (
    <div className={Style.perksContainer}>
      {perks.map(perk => {
        const { text } = perk;
        return (
          <div className={Style.perkContainer} key={`perk-separator-container-${text}`}>
            <div className={Style.perkInnerContainer} key={`perk-${text}`}>
              <div className={Style.perkImageContainer}>
                <img
                  className={Style.perkIcon}
                  src="/static/images/foxnation-freemium-logo.png"
                  alt="Fox News logo"
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return {
    unlockUpsellText: <div className={Style.unlockUpsellText}>{unlockUpsellText}</div>,
    unlockUpsellInButtonText,
    unlockUpsellPerks,
  };
}

export function getCookiesBlockedProps({ options }) {
  const {
    playback_cookieErrorHeader: headerText,
    playback_cookieErrorMessage: descriptionText,
    global_signInButton: signInButtonText,
  } = window.Language;
  const { theme, showCookiesBlocked, resetShowCookiesBlocked } = options;

  const headerTag = (
    <div key="header" className={theme.header}>
      {headerText}
    </div>
  );

  const descriptionTag = (
    <div key="description" className={theme.description}>
      {helperMarkdownToComponent(descriptionText)}
    </div>
  );

  const getButtonTag = ({ onSignInButtonClick }) => (
    <button
      onClick={() => {
        onSignInButtonClick();
        resetShowCookiesBlocked();
      }}
    >
      {signInButtonText}
    </button>
  );

  const getCookiesBlockedChildren = ({ onSignInButtonClick }) => {
    const hasButton = !!onSignInButtonClick;
    return [headerTag, descriptionTag, hasButton ? getButtonTag({ onSignInButtonClick }) : null];
  };

  return {
    getCookiesBlockedChildren,
    cookiesBlockedTheme: theme,
    renderCookiesBlocked: showCookiesBlocked,
  };
}

export function getFailureProps({ sharedTheme, theme }) {
  const {
    auth_failedHeader: headerText,
    auth_failedMessage: descriptionText,
    authFailedContinueButton: buttonText,
  } = window.Language;

  const confirmTag = ({ onConfirmClick }) => (
    <div key="confirm-button" className={sharedTheme.confirmContainer}>
      <button onClick={onConfirmClick} className={sharedTheme.confirm} type="button">
        <span>{buttonText}</span>
      </button>
    </div>
  );
  confirmTag.propTypes = {
    onConfirmClick: PropTypes.func.isRequired,
  };

  const headerTag = (
    <div key="header" className={theme.header}>
      {headerText}
    </div>
  );

  const descriptionTag = (
    <div key="description" className={theme.description}>
      {descriptionText}
    </div>
  );

  const contentChildren = ({ onConfirmClick }) => [
    headerTag,
    descriptionTag,
    confirmTag({ onConfirmClick }),
  ];

  return {
    contentChildren,
    failureTheme: theme,
  };
}

export function getContentBlackoutProps({ theme }) {
  const {
    blackout_default_title: defaultHeaderText,
    blackout_default: defaultDescriptionText,
  } = window.Language;

  const headerTag = headerTextOverride => (
    <div key="header" className={theme.header}>
      {headerTextOverride || defaultHeaderText}
    </div>
  );

  const descriptionTag = descriptionTextOverride => (
    <div key="description" className={theme.description}>
      {descriptionTextOverride || defaultDescriptionText}
    </div>
  );

  const contentBlackoutChildren = (headerTextOverrideKey, descriptionTextOverrideKey) => {
    const headerText = _.get(window.Language, headerTextOverrideKey);
    const descriptionText = _.get(window.Language, descriptionTextOverrideKey);

    return [headerTag(headerText), descriptionTag(descriptionText)];
  };

  return {
    contentBlackoutChildren,
    contentBlackoutTheme: theme,
  };
}

export function getPlaybackErrorProps({ theme, provider }) {
  const isComcast = _.get(provider, 'adobePassId') === PROVIDER_COMCAST;
  const {
    pcs_ctaButtonLabel: label,
    concurrency_error_button,
    concurrency_error_detail,
    concurrency_error_header,
  } = window.Language;
  const { pcsComcastInfo: link } = window.ExternalConfig;
  const playbackErrorChildren = ({ errorText, concurrencyError }) => {
    if (concurrencyError) {
      return (
        <div className={`${theme.concurrencyContainer} ft-playback-error`}>
          <div className={theme.logo}>
            <NetworkLink theme={theme} preauthorizedResources={this.props.preauthorizedResources} />
          </div>
          <div className={theme.headerText}>{concurrency_error_header}</div>
          <div className={theme.bodyText}>{concurrency_error_detail}</div>
          <div className={theme.actions}>
            <button className={theme.refreshButton} onClick={refreshPage}>
              {concurrency_error_button}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className={`${theme.errorContainer} ft-playback-error`}>
        <div className={theme.headerText}>{_.get(errorText, 'header')}</div>
        <div className={theme.bodyText}>{_.get(errorText, 'body')}</div>
        {isComcast && (
          <div className={theme.actions}>
            <a href={link} target="_blank" rel={LINK_REL}>
              <button type="button">{label}</button>
            </a>
          </div>
        )}
      </div>
    );
  };
  playbackErrorChildren.propTypes = {
    errorText: PropTypes.shape({
      header: PropTypes.any,
      body: PropTypes.any,
    }),
    concurrencyError: PropTypes.bool,
  };
  playbackErrorChildren.defaultProps = {
    errorText: {},
    concurrencyError: false,
  };
  return {
    playbackErrorChildren,
    playbackErrorTheme: theme,
  };
}

export function getPreviewPassWelcomeProps({ theme }) {
  const {
    previewPass_welcome_heading_60_min: heading_60_min,
    previewPass_welcome_heading_5_min: heading_5_min,
  } = window.Language;

  const previewPassWelcomeText = ({ mvpd }) => {
    if (!mvpd) return null;
    const heading = mvpd === PREVIEW_PASS_5 ? heading_5_min : heading_60_min;
    return <div className={theme.previewPassWelcomeText}>{heading}</div>;
  };
  const image = getPreviewPassImage();

  return {
    previewPassWelcomeText,
    previewPassWelcomeNetworkLogo: image,
    previewPassWelcomeTheme: theme,
  };
}

export function getProfilePcsErrorProps({ theme }) {
  const profilePcsErrorChildren = ({ errorText }) => (
    <div className={`${theme.errorContainer} ft-playback-error`}>
      <div className={theme.headerText}>{_.get(errorText, 'header')}</div>
      <div className={theme.bodyText}>{_.get(errorText, 'body')}</div>
    </div>
  );
  profilePcsErrorChildren.propTypes = {
    errorText: PropTypes.shape({
      header: PropTypes.any,
      body: PropTypes.any,
    }),
  };
  profilePcsErrorChildren.defaultProps = {
    errorText: {},
  };

  return {
    profilePcsErrorChildren,
    profilePcsErrorTheme: theme,
  };
}

export function getGeoLocationErrorProps({ theme, isGeolocationEnabled }) {
  if (!isGeolocationEnabled) return null;
  const geoLocationErrorChildren = ({ primaryActionCallback, geoLocationTimeoutFail }) => {
    const {
      livePlayer_geoLocationErrorHeader: headerText,
      livePlayer_geoLocationErrorBody: bodyText,
      livePlayer_geoLocationFailBody: failBodyText,
      livePlayer_geoLocationErrorPrompt: actionButtonText,
      supportCta: linkText,
    } = window.Language;

    const errorText = geoLocationTimeoutFail ? failBodyText : bodyText;

    return (
      <GeolocationError
        headerText={headerText}
        bodyText={errorText}
        actionButtonText={actionButtonText}
        linkText={linkText}
        primaryActionCallback={primaryActionCallback}
        geoLocationTimeoutFail={geoLocationTimeoutFail}
      />
    );
  };

  return {
    geoLocationErrorChildren,
    playbackErrorTheme: theme,
  };
}

export function getNoSportsOnMobileProps({ theme }) {
  const noSportsOnMobileErrorChildren = ({ errorText, primaryActionCallback }) => (
    <div className={theme.errorContainer}>
      <div className={theme.headerText}>{_.get(errorText, 'header')}</div>
      <div className={theme.bodyText}>{_.get(errorText, 'body')}</div>
      <div className={theme.actions}>
        {!primaryActionCallback ? null : (
          <button
            onClick={() => {
              primaryActionCallback();
            }}
            className={theme.primaryAction}
            type="button"
          >
            {_.get(errorText, 'primaryAction')}
          </button>
        )}
      </div>
    </div>
  );
  noSportsOnMobileErrorChildren.propTypes = {
    errorText: PropTypes.shape({
      header: PropTypes.any,
      body: PropTypes.any,
      primaryAction: PropTypes.any,
    }),
    primaryActionCallback: PropTypes.func,
  };
  noSportsOnMobileErrorChildren.defaultProps = {
    errorText: {},
    primaryActionCallback: () => {},
  };

  return {
    noSportsOnMobileErrorChildren,
    playbackErrorTheme: theme,
  };
}

export function getNotEntitledProps({ sharedTheme, theme, onLogout }) {
  const {
    auth_notEntitledButton: notEntitledCTAButtonText,
    auth_notEntitledError,
    auth_notEntitledInfo,
  } = window.Language;

  let notEntitledErrorText;
  let notEntitledErrorInfoText;

  const closeOverlay = async () => {
    if (isMvpdSubscription()) {
      redirectToExternalUrl(helperGetHomeUrl());
    } else {
      await onLogout();
      window.location.reload();
    }
  };

  const { preauthorizedResources, provider } = this.props;

  if (provider) {
    notEntitledErrorText = auth_notEntitledError;
    notEntitledErrorInfoText = auth_notEntitledInfo;
  }

  const headerBarTag = () => (
    <div key="header-bar" className={sharedTheme.headerBarExtraPadding}>
      <div className={sharedTheme.headerContainer}>
        <div key="logo" className={sharedTheme.headerLogo}>
          <NetworkLink
            theme={sharedTheme}
            alt="Fox Nation logo"
            clickEvent={() => {
              closeOverlay();
            }}
            preauthorizedResources={preauthorizedResources}
          />
        </div>
        <button
          aria-label="close"
          key="close"
          onClick={() => {
            closeOverlay();
          }}
          className={sharedTheme.close}
          type="button"
        />
      </div>
    </div>
  );

  const notEntitledCTAButton = () => (
    <button onClick={closeOverlay} className={theme.notEntitledCTAButton} type="button">
      {notEntitledCTAButtonText}
    </button>
  );

  const secondaryLogo = _.get(provider, 'logos[0].url');
  const providerLogoUrl = forceHttps(_.get(provider, 'logos[1].url', secondaryLogo));
  const providerLogoImg = providerLogoUrl ? (
    <img src={providerLogoUrl} alt={_.get(provider, 'name', 'Cable Provider Logo')} />
  ) : null;

  const logo = <div className={theme.notEntitledLogo}>{providerLogoImg}</div>;
  const error = <div className={theme.notEntitledError}>{notEntitledErrorText}</div>;
  const info = <div className={theme.notEntitledErrorInfo}>{notEntitledErrorInfoText}</div>;

  const notEntitledChildren = () => {
    const content = (
      <div className={classNames(sharedTheme.content, theme.errorDetails)}>
        {logo}
        {error}
        {info}
        {notEntitledCTAButton()}
      </div>
    );
    return [headerBarTag(), content];
  };

  return {
    notEntitledChildren,
    notEntitledTheme: theme,
  };
}

export function getSuccessProps({ sharedTheme, theme, networkList }) {
  const {
    auth_successTitle: titleText,
    auth_allAccessMessage: allAccessText,
    auth_limitedAccessMessage: limitedAccessText,
    auth_noEntitlements: noAccessText,
    auth_successButton: buttonText,
  } = window.Language;

  const closeOverlay = () => {
    window.location.reload();
  };

  const headerBarTag = (
    <div key="header-bar" className={sharedTheme.headerBarExtraPadding}>
      <div className={sharedTheme.headerContainer}>
        <div key="logo" className={sharedTheme.headerLogo}>
          <NetworkLink
            theme={sharedTheme}
            alt="Fox Nation logo"
            clickEvent={() => {
              closeOverlay();
            }}
          />
        </div>
        <button
          aria-label="close"
          key="close"
          onClick={() => {
            closeOverlay();
          }}
          className={sharedTheme.close}
          type="button"
        />
      </div>
    </div>
  );
  const headerTag = ({ preauthorizedResources }) => {
    const authResourcesLength = _.size(preauthorizedResources);
    const networkListLength = _.size(networkList);
    const isFullAuth = authResourcesLength >= networkListLength && authResourcesLength !== null;
    const isPartialAuth =
      authResourcesLength < networkListLength &&
      authResourcesLength !== 0 &&
      authResourcesLength !== null;
    if (!isFullAuth && !isPartialAuth) {
      return null;
    }
    return (
      <div key="header-text" className={classNames(sharedTheme.headerText, theme.headerText)}>
        {titleText}
      </div>
    );
  };
  const noAccessTextAddOn = (
    <span>
      &nbsp;Just look for shows without the
      <div className={theme.noAccessLockIcon}>
        <Icon icon="lock" color="white" size="full" viewBox="1 1 36 36" />
      </div>
      icon.
    </span>
  );
  const descriptionTag = ({ preauthorizedResources }) => {
    const authResourcesLength = _.size(preauthorizedResources);
    const networkListLength = _.size(networkList);
    const isFullAuth = authResourcesLength >= networkListLength && authResourcesLength !== null;
    const isPartialAuth =
      authResourcesLength < networkListLength &&
      authResourcesLength !== 0 &&
      authResourcesLength !== null;

    const accessText =
      (isFullAuth && allAccessText) || (isPartialAuth && limitedAccessText) || noAccessText;

    const specialFormatting = isFullAuth ? null : theme.descriptionTextLimitedAccess;

    return (
      <div
        key="description"
        className={classNames(
          sharedTheme.descriptionText,
          theme.descriptionText,
          specialFormatting,
          !isFullAuth && !isPartialAuth && theme.noAccessDescription,
        )}
      >
        {accessText}
        {!isFullAuth && !isPartialAuth && noAccessTextAddOn}
      </div>
    );
  };

  descriptionTag.propTypes = {
    preauthorizedResources: PropTypes.arrayOf(PropTypes.string).isRequired,
  };

  const networksTag = ({ preauthorizedResources, networkList }) => {
    const networks = networkList.map(item => {
      const isAuthorized = isContentAuthorized(
        preauthorizedResources,
        [item],
        getAuthNetworksConfig(),
      );
      const url = getNetworkLogoUrl(item, 35);
      if (!url) return null;
      let containerClass = theme.networkLogoContainer;
      if (!isAuthorized) {
        containerClass = classNames(containerClass, theme.networkLogoContainerFade);
      }
      const lockIcon = !isAuthorized && (
        <div className={theme.lockIcon}>
          <Icon icon="lock" color="white" size="full" viewBox="1 1 36 36" />
        </div>
      );
      const networkLogo = (
        <div className={theme.logoImage}>
          <img
            className={theme.networkLogoImage}
            key={`network-${item}-img`}
            src={url}
            alt={`${item} logo`}
          />
        </div>
      );
      return (
        <div key={item} className={containerClass}>
          {lockIcon}
          {networkLogo}
        </div>
      );
    });

    const data = (
      <div key="networks" className={theme.networkContainer2}>
        {networks}
      </div>
    );

    return data;
  };

  const continueButton = (
    <button
      className={theme.authSuccessButton}
      onClick={() => {
        closeOverlay();
      }}
    >
      Continue
    </button>
  );

  networksTag.propTypes = {
    preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
    networkList: PropTypes.arrayOf(PropTypes.string),
    mvpd: PropTypes.string,
  };
  networksTag.defaultProps = {
    preauthorizedResources: [],
    networkList: [],
    mvpd: null,
  };

  const innerContainerChildren = ({ preauthorizedResources, mvpd }) => [
    headerBarTag,
    headerTag({ preauthorizedResources }),
    descriptionTag({ preauthorizedResources }),
    networksTag({ preauthorizedResources, networkList, mvpd }),
    continueButton,
  ];

  return {
    successInnerContainerChildren: innerContainerChildren,
    successButtonItem: buttonText,
    successTheme: theme,
  };
}

export function getLoadingProps({ sharedTheme, theme, onOverlayClose }) {
  const { auth_loadingTitle: titleText } = window.Language;
  const { preauthorizedResources } = this.props;
  const headerBarTag = ({ onClose }) => (
    <div key="header-bar" className={sharedTheme.headerBarExtraPadding}>
      <div className={sharedTheme.headerContainer}>
        <div key="logo" className={sharedTheme.headerLogo}>
          <NetworkLink
            theme={sharedTheme}
            alt="Fox Nation logo"
            clickEvent={() => {
              onClose();
            }}
            preauthorizedResources={preauthorizedResources}
          />
        </div>
        <button
          aria-label="close"
          key="close"
          onClick={() => {
            onClose();
          }}
          className={sharedTheme.close}
          type="button"
        />
      </div>
    </div>
  );

  headerBarTag.propTypes = {
    onClose: PropTypes.func.isRequired,
  };

  const headerTag = (
    <div key="header-text" className={classNames(sharedTheme.content, theme.content)}>
      <div className={classNames(sharedTheme.headerText, theme.headerText)}>{titleText}</div>
      <Loader />
    </div>
  );

  const loadingOnClose = () => {
    if (onOverlayClose) onOverlayClose();
  };

  const innerContainerChildren = ({ onClose }) => [headerBarTag({ onClose }), headerTag];

  return {
    loadingInnerContainerChildren: innerContainerChildren,
    loadingBackgroundClass: 'withBackground',
    loadingTheme: theme,
    loadingOnClose,
  };
}

export function getMVPDSearchProps({
  theme,
  sharedTheme,
  topMVPDProviders,
  mvpdProviders,
  account,
  onOverlayClose,
  previewPassExpired,
  previewPassSignIn,
}) {
  const {
    auth_searchProviderHeader: headerBarText,
    auth_searchProviderTopProvidersHeader: providersText,
  } = window.Language;
  const profileId = _.get(account, 'profileId');
  const mvpdSearchInnerContainerChildren = ({ onClose, onBack }) => {
    const headerBarTag = (
      <div key="header-bar" className={classNames(sharedTheme.headerBarBorder, theme.headerBar)}>
        <div key="logo" className={classNames(sharedTheme.headerLogo, sharedTheme.hideMobile)}>
          <NetworkLink
            theme={sharedTheme}
            alt="Fox Nation logo"
            clickEvent={() => {
              onClose();
            }}
            preauthorizedResources={this.props.preauthorizedResources}
          />
        </div>
        <Link
          key="back"
          onClick={() => {
            onBack();
          }}
          className={classNames(theme.back, theme.hideDesktop)}
        />
        <div className={theme.headerBarText}>{headerBarText}</div>
        <button
          aria-label="close"
          key="close"
          onClick={() => {
            if (onClose) onClose();
          }}
          className={sharedTheme.close}
          type="button"
        />
      </div>
    );
    return [headerBarTag];
  };
  const providerTags = (onClick, selectedChannel, providers) => {
    if (!providers || !providers.length) return null;
    return providers.map(provider => {
      const { name, adobePassId, '@id': providerId } = provider;
      return (
        <div className={theme.providerContainer} key={`provider-${providerId}`}>
          <Link
            className={theme.provider}
            onClick={() => {
              const channel = isLive && selectedChannel ? selectedChannel : null;
              const id = adobePassId;
              const cookie = {
                name: SUCCESS_COOKIE_NAME,
                value: {
                  selectedChannel: channel,
                  provider: id,
                },
                options: {
                  path: '/',
                },
              };
              onClick(id, cookie);
              const action = 'authentication_select_provider';
              const authStartSource = getTealiumSourceAuth() || LOCKED_CONTENT_SOURCE;
              const onboardSrc = checkAuthStartSource(previewPassSignIn, previewPassExpired);
              const onboardingStartSource = onboardSrc;
              setSegmentPageStartSource({ previewPassSignIn, previewPassExpired });
              const data = setTealiumProfileId(profileId, {
                action,
                auth_start_source: authStartSource,
                dcg_profile_id: profileId,
                mvpd_select_type: 'other mvpd',
                mvpd: id,
                onboarding_action: TealiumOnboardingActions.COMPLETE,
                onboarding_start_source: onboardingStartSource,
                platform_version: BUILD_VERSION,
                profile_start_source: undefined,
                ...getCommonTealiumProps(),
              });
              callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
            }}
          >
            {name}
          </Link>
        </div>
      );
    });
  };
  const providersTag = ({
    onProviderClick,
    selectedChannel,
    providers,
    header,
    containerClass,
  }) => (
    <div key="providers" className={containerClass}>
      {header}
      {providerTags(onProviderClick, selectedChannel, providers)}
    </div>
  );
  providersTag.propTypes = {
    onProviderClick: PropTypes.func,
    selectedChannel: PropTypes.number,
    providers: PropTypes.arrayOf(PropTypes.shape({})),
    header: PropTypes.shape({}),
    containerClass: PropTypes.string,
  };
  providersTag.defaultProps = {
    onProviderClick: () => {},
    selectedChannel: null,
    providers: [],
    header: {},
    containerClass: null,
  };

  const mvpdSearchInput = ({ input, onInputChange, onBackClick }) => (
    <div key="search-input">
      <div className={theme.desktopBack}>
        <Link
          onClick={onBackClick}
          className={classNames(theme.back, theme.hideMobile, theme.backDesktop)}
        />
      </div>
      <div className={theme.inputContainer}>
        <input
          type="text"
          className={theme.input}
          placeholder="Search"
          value={input}
          onChange={onInputChange}
        />
      </div>
    </div>
  );
  mvpdSearchInput.propTypes = {
    input: PropTypes.string.isRequired,
    onInputChange: PropTypes.func.isRequired,
    onBackClick: PropTypes.func.isRequired,
  };
  const mvpdSearchResults = ({ onProviderClick, selectedChannel, input }) => {
    const { auth_noProvidersText: noProvidersText } = window.Language;
    const previewPasses = [PREVIEW_PASS_5, PREVIEW_PASS_60];
    const previewPassRemovedProviders = _.clone(mvpdProviders);
    _.remove(previewPassRemovedProviders, mvpd => {
      const id = _.get(mvpd, 'adobePassId');
      return _.includes(previewPasses, id);
    });
    const filteredProviders = _.filter(previewPassRemovedProviders, provider => {
      const lowercaseInput = input.toLowerCase();
      const name = _.get(provider, 'name', '').toLowerCase();
      const alternateNames = _.get(provider, 'alternateNames', []);
      const nameMatch = name.indexOf(lowercaseInput) > -1;
      const alternateNamesMatch = _.find(
        alternateNames,
        n => n.toLowerCase().indexOf(lowercaseInput) > -1,
      );
      return nameMatch || alternateNamesMatch || false;
    }).slice(0, 7);
    const isResults = !!filteredProviders.length && !!input;
    const isEmptyFilteredResults = !filteredProviders.length && !!input;
    const defaultProviders =
      topMVPDProviders && topMVPDProviders.length ? topMVPDProviders.slice(0, 7) : topMVPDProviders;
    const defaultProvidersTags = providersTag({
      onProviderClick,
      selectedChannel,
      providers: defaultProviders,
      header: <div className={theme.providersText}> {providersText} </div>,
      containerClass: theme.providersContainer,
    });
    const filteredProvidersTags = isResults
      ? providersTag({
          onProviderClick,
          selectedChannel,
          providers: filteredProviders,
          containerClass: theme.filteredProvidersContainer,
        })
      : null;
    const noProvidersTags = (
      <div className={theme.filteredProvidersContainer}>
        <div className={theme.providerContainer}>{noProvidersText}</div>
      </div>
    );
    let renderTags;

    if (isResults) {
      renderTags = filteredProvidersTags;
    } else if (isEmptyFilteredResults) {
      renderTags = noProvidersTags;
    } else {
      renderTags = defaultProvidersTags;
    }

    return (
      <div key="search-results" className={theme.resultsContainer}>
        {renderTags}
      </div>
    );
  };
  mvpdSearchResults.propTypes = {
    onProviderClick: PropTypes.func.isRequired,
    selectedChannel: PropTypes.number,
    input: PropTypes.string,
  };
  mvpdSearchResults.defaultProps = {
    selectedChannel: null,
    input: null,
  };

  const needHelpTag = (
    <div key="need-help" className={theme.helpLinkContainer}>
      {generateLinkTag(theme, _.get(window.ExternalConfig, 'auth.playerLinks.needHelp', null))}
    </div>
  );
  const mvpdSearchChildren = [needHelpTag];
  function mvpdSearchOnInputChange(event) {
    const value = _.get(event, 'target.value', '');
    this.setState({ input: value });
  }

  return {
    mvpdSearchTheme: theme,
    mvpdSearchInnerContainerChildren,
    mvpdSearchBackgroundClass: 'withBackground',
    mvpdSearchInput,
    mvpdSearchResults,
    mvpdSearchOnInputChange,
    mvpdSearchChildren,
    mvpdSearchOnClose: () => {
      let action = 'authentication_incomplete';
      const authStartSource = getTealiumSourceAuth() || LOCKED_CONTENT_SOURCE;
      setTealiumSourceAuth(null);
      setTealiumProfileStartSource(null);
      let data = setTealiumProfileId(profileId, {
        action,
        auth_start_source: authStartSource,
        dcg_profile_id: profileId,
        error_message: 'auth flow aborted',
        platform_version: BUILD_VERSION,
        profile_start_source: undefined,
        sso_permission: 'not allowed',
        ...getCommonTealiumProps(),
      });
      callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
      optimizelyUserAttributeEvents().authState(ATTEMPTED_AUTH);
      action = 'authentication_select_provider';
      const onboardSrc = checkAuthStartSource(previewPassSignIn, previewPassExpired);
      const onboardingStartSource = onboardSrc;
      data = setTealiumProfileId(profileId, {
        action,
        auth_start_source: authStartSource,
        dcg_profile_id: profileId,
        mvpd_select_type: 'primary mvpd list',
        mvpd: 'no mvpd',
        onboarding_action: TealiumOnboardingActions.SKIP,
        onboarding_start_source: onboardingStartSource,
        platform_version: BUILD_VERSION,
        profile_start_source: undefined,
        ...getCommonTealiumProps(),
      });
      callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
      if (onOverlayClose) onOverlayClose();
    },
  };
}

export function getMVPDIframeProps({ theme, sharedTheme }) {
  const mvpdIframeInnerContainerChildren = ({ onClose }) => {
    const headerBarTag = (
      <div key="header-bar" className={sharedTheme.headerBarExtraPadding}>
        <div className={sharedTheme.headerContainer}>
          <div key="logo" className={sharedTheme.headerLogo}>
            <NetworkLink
              theme={sharedTheme}
              alt="Fox Nation logo"
              clickEvent={() => {
                onClose();
              }}
            />
          </div>
          <button
            aria-label="close"
            key="close"
            onClick={() => {
              onClose();
            }}
            className={sharedTheme.close}
            type="button"
          />
        </div>
      </div>
    );
    return [headerBarTag];
  };
  return {
    mvpdIframeTheme: theme,
    mvpdIframeInnerContainerChildren,
  };
}

export function getMVPDStartProps({
  accessToken,
  account,
  deviceId,
  mvpdLogin,
  onOverlayClose,
  previewPassExpired,
  previewPassSignIn,
  profile,
  setAuthorizationToken,
  setMVPDToAppState,
  sharedTheme,
  showEmailForm,
  showUpsellProgressBar,
  theme,
  topMVPDProviders,
}) {
  const { preauthorizedResources } = this.props;
  const profileId = _.get(account, 'profileId');
  const mvpdStartChildren = ({
    onProviderClick,
    onAllProvidersClick,
    onClose,
    onSuccessClose,
    selectedChannel,
  }) => {
    if (isMVPDSSOEnabled()) {
      const mvpdProviderProps = {
        accessToken,
        deviceId,
        onSuccessClose,
        profile,
        mvpdLogin,
        setAuthorizationToken,
        selectedChannel,
        setMVPDToAppState,
      };
      return [<MvpdProviders {...mvpdProviderProps} />];
    }
    return [
      <MVPDStartProviders
        onProviderClick={onProviderClick}
        accessToken={accessToken}
        account={account}
        deviceId={deviceId}
        onClose={onClose}
        onOverlayClose={onOverlayClose}
        previewPassExpired={previewPassExpired}
        previewPassSignIn={previewPassSignIn}
        sharedTheme={sharedTheme}
        showEmailForm={showEmailForm}
        topMVPDProviders={topMVPDProviders}
        onAllProvidersClick={onAllProvidersClick}
        isLive={isLive}
        selectedChannel={selectedChannel}
      />,
    ];
  };
  const mvpdStartInnerContainerChildren = ({ onClose }) => {
    const headerBarTag = (
      <div key="header-bar" className={sharedTheme.headerBarExtraPadding}>
        <div className={sharedTheme.headerContainer}>
          <div key="logo" className={sharedTheme.headerLogo}>
            <NetworkLink
              theme={sharedTheme}
              alt="Fox Nation logo"
              clickEvent={() => {
                onClose();
              }}
              preauthorizedResources={preauthorizedResources}
            />
          </div>
          {showUpsellProgressBar && <UpsellProgressBar />}
          <button
            aria-label="close"
            key="close"
            onClick={() => {
              onClose();
            }}
            className={sharedTheme.close}
            type="button"
          />
        </div>
      </div>
    );
    return [headerBarTag];
  };

  return {
    mvpdStartTheme: theme,
    mvpdStartChildren,
    mvpdStartInnerContainerChildren,
    mvpdStartBackgroundClass: 'withBackground',
    mvpdStartOnClose: () => {
      const action = 'authentication_incomplete';
      const authStartSource = getTealiumSourceAuth() || LOCKED_CONTENT_SOURCE;
      setTealiumSourceAuth(null);
      setTealiumProfileStartSource(null);
      const data = setTealiumProfileId(profileId, {
        action,
        auth_start_source: authStartSource,
        dcg_profile_id: profileId,
        error_message: 'auth flow aborted',
        mvpd: 'no mvpd',
        platform_version: BUILD_VERSION,
        profile_start_source: undefined,
        sso_permission: 'not allowed',
        ...getCommonTealiumProps(),
      });
      callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
      optimizelyUserAttributeEvents().authState(ATTEMPTED_AUTH);
      if (onOverlayClose) onOverlayClose();
    },
  };
}

export function getMVPDIncompleteProps({ theme }) {
  const {
    playback_mvpdAuthIncompleteHeader: headerText,
    playback_mvpdAuthIncompleteMessage: descriptionText,
    global_signInButton: signInButtonText,
  } = window.Language;

  const signInButton = buttonOnClick => (
    <button onClick={buttonOnClick} className={theme.mvpdIncompleteButton} type="button">
      {signInButtonText}
    </button>
  );

  const headerTag = (
    <div key="mvpdIncompleteHeader" className={theme.mvpdIncompleteHeading}>
      {headerText}
    </div>
  );

  const descriptionTag = (
    <div key="mvpdIncompleteDescription" className={theme.mvpdIncompleteSubheading}>
      {descriptionText}
    </div>
  );

  const mvpdIncompleteChildren = buttonOnClick => [
    headerTag,
    descriptionTag,
    signInButton(buttonOnClick),
  ];

  return {
    mvpdIncompleteChildren,
    mvpdIncompleteTheme: theme,
  };
}

export function getMVPDRetryProps({ sharedTheme, theme }) {
  const {
    mvpdSignIn_error: signInErrorText,
    mvpdSignIn_retry: retryText,
    mvpdSignIn_other: tryOtherProviderText,
  } = window.Language;
  const { preauthorizedResources } = this.props;
  const headerBarTag = onClose => (
    <div key="header-bar" className={sharedTheme.headerBarExtraPadding}>
      <div className={sharedTheme.headerContainer}>
        <div key="logo" className={sharedTheme.headerLogo}>
          <NetworkLink
            theme={sharedTheme}
            alt="Fox Nation logo"
            clickEvent={() => {
              onClose();
            }}
            preauthorizedResources={preauthorizedResources}
          />
        </div>
        <button
          aria-label="close"
          key="close"
          onClick={() => {
            onClose();
          }}
          className={sharedTheme.close}
          type="button"
        />
      </div>
    </div>
  );

  const tryAgainButton = buttonOnClick => (
    <button onClick={buttonOnClick} className={theme.mvpdRetryButton} type="button">
      {retryText}
    </button>
  );

  const otherProviderButton = onClick => (
    <span onClick={onClick} className={theme.maybeLaterLink}>
      {tryOtherProviderText}
    </span>
  );

  const headerTag = (
    <div key="mvpdRetryHeader" className={theme.mvpdRetryHeading}>
      {signInErrorText}
    </div>
  );

  const mvpdRetryChildren = ({ retryButtonClick, otherProviderClick, onCloseClick }) => {
    const content = (
      <div className={classNames(sharedTheme.content, theme.errorDetails)}>
        {headerTag}
        {tryAgainButton(retryButtonClick)}
        {otherProviderButton(otherProviderClick)}
      </div>
    );
    return [headerBarTag(onCloseClick), content];
  };

  return {
    mvpdRetryChildren,
    mvpdRetryTheme: theme,
  };
}

export function getUpsellErrorProps({ onAuthCheckComplete, onOverlayClose, sharedTheme, theme }) {
  const { preauthorizedResources } = this.props;
  const upsellErrorOnClose = () => {
    if (onOverlayClose) onOverlayClose();
    if (onAuthCheckComplete) onAuthCheckComplete({ complete: true, success: true });
  };

  const upsellErrorChildren = ({ onClick, upsellConfirmClicked }) => {
    const {
      auth_ineligibleHeader: headerTextIneligible,
      auth_ineligibleMessage: descriptionTextIneligible,
      authFailedContinueButton: buttonTextIneligible,
      auth_confirmSubscriptionErrorHeader: headerTextConfirmError,
      auth_confirmSubscriptionErrorMessage: descriptionTextConfirmError,
      auth_confirmSubscriptionProviderCTA: buttonTextConfirmError,
    } = window.Language;

    let headerText;
    let descriptionText;
    let buttonText;

    if (upsellConfirmClicked) {
      headerText = headerTextConfirmError;
      descriptionText = descriptionTextConfirmError;
      buttonText = buttonTextConfirmError;
    } else {
      headerText = headerTextIneligible;
      descriptionText = descriptionTextIneligible;
      buttonText = buttonTextIneligible;
    }

    const headerTextTag = (
      <div key="header-text" className={classNames(sharedTheme.headerText, theme.headerText)}>
        {headerText}
      </div>
    );

    const descriptionTextTag = (
      <div
        key="description-text"
        className={classNames(sharedTheme.descriptionText, theme.descriptionText)}
      >
        {helperMarkdownToComponent(descriptionText)}
      </div>
    );

    const actionButtonTag = (
      <div
        key="confirm-button"
        className={classNames(sharedTheme.confirmContainer, theme.confirmContainer)}
      >
        <button
          onClick={onClick}
          className={classNames(sharedTheme.confirm, theme.confirm)}
          type="button"
        >
          <span>{buttonText}</span>
        </button>
      </div>
    );

    return [headerTextTag, descriptionTextTag, actionButtonTag];
  };

  const upsellErrorInnerContainerChildren = ({ onClose }) => {
    const headerBarTag = (
      <div key="header-bar" className={sharedTheme.headerBarExtraPadding}>
        <div className={sharedTheme.headerContainer}>
          <div key="logo" className={sharedTheme.headerLogo}>
            <NetworkLink
              theme={sharedTheme}
              alt="Fox Nation logo"
              clickEvent={() => {
                onClose();
              }}
              preauthorizedResources={preauthorizedResources}
            />
          </div>
          <button
            aria-label="close"
            key="close"
            onClick={() => {
              onClose();
            }}
            className={sharedTheme.close}
            type="button"
          />
        </div>
      </div>
    );
    return [headerBarTag];
  };
  return {
    upsellErrorTheme: theme,
    upsellErrorChildren,
    upsellErrorBackgroundClass: 'withBackground',
    upsellErrorInnerContainerChildren,
    upsellErrorOnClose,
  };
}

export function getUpsellConfirmProps({ account, onOverlayClose, sharedTheme, theme, profileId }) {
  const { preauthorizedResources } = this.props;
  const upsellConfirmOnClose = () => {
    if (onOverlayClose) onOverlayClose();
  };

  const {
    auth_confirmSubscriptionHeader: headerText,
    auth_confirmSubscriptionMessage: descriptionText,
    auth_confirmSubscriptionButton: buttonText,
  } = window.Language;

  const upsellConfirmChildren = ({ onClick }) => {
    const upsellConfirmButtonClick = () => {
      onClick();
      const action = 'authentication_start';
      setTealiumProfileStartSource('other');
      const data = setTealiumProfileId(profileId, {
        action,
        dcg_profile_id: profileId,
        mvpd: 'no mvpd',
        onboarding_start_source: 'none',
        platform_version: BUILD_VERSION,
        profile_start_source: 'other',
        ...getCommonTealiumProps(),
      });
      callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
    };
    const headerTextTag = (
      <div key="header-text" className={classNames(sharedTheme.headerText, theme.headerText)}>
        {headerText}
      </div>
    );

    const descriptionTextTag = (
      <div
        key="description-text"
        className={classNames(sharedTheme.descriptionText, theme.descriptionText)}
      >
        {descriptionText}
      </div>
    );

    const actionButtonTag = (
      <div
        key="confirm-button"
        className={classNames(sharedTheme.confirmContainer, theme.confirmContainer)}
      >
        <button
          onClick={upsellConfirmButtonClick}
          className={classNames(sharedTheme.confirm, theme.confirm)}
          type="button"
        >
          <span>{buttonText}</span>
        </button>
      </div>
    );

    return [headerTextTag, descriptionTextTag, actionButtonTag];
  };

  const upsellConfirmInnerContainerChildren = ({ onClose }) => {
    const headerBarTag = (
      <div key="header-bar" className={sharedTheme.headerBarExtraPadding}>
        <div className={sharedTheme.headerContainer}>
          <div key="logo" className={sharedTheme.headerLogo}>
            <NetworkLink
              theme={sharedTheme}
              alt="Fox Nation logo"
              clickEvent={() => {
                onClose();
              }}
              preauthorizedResources={preauthorizedResources}
            />
          </div>
          <button
            aria-label="close"
            key="close"
            onClick={() => {
              onClose();
            }}
            className={sharedTheme.close}
            type="button"
          />
        </div>
      </div>
    );
    return [headerBarTag];
  };
  return {
    upsellConfirmTheme: theme,
    upsellConfirmChildren,
    upsellConfirmBackgroundClass: 'withBackground',
    upsellConfirmInnerContainerChildren,
    upsellConfirmOnClose,
  };
}

export function getAdditionalAuthorizedNetworks() {
  const authNetworksConfig = _.get(window.ExternalConfig, 'auth.networks');
  const additionalAuthorizedNetworks = {};
  _.forOwn(authNetworksConfig, (networkInfo, network) => {
    if (networkInfo.additionalAuthorizedNetworks.length) {
      additionalAuthorizedNetworks[network] = networkInfo.additionalAuthorizedNetworks;
    }
  });
  return additionalAuthorizedNetworks;
}

export const getProviderObjectFromList = (mvpdProvidersList, mvpdId) =>
  _.find(mvpdProvidersList, { adobePassId: mvpdId });

export function findProvider(mvpdProvidersList, account, mvpdId) {
  const providerObject = getProviderObjectFromList(mvpdProvidersList, mvpdId);
  if (isPreviewPass(mvpdId)) {
    // check if our cookie exists, if it does do nothing
    // if no cookie, set a cookie and call the started event
    const previewPassCookie = getCookie({ name: PREVIEW_PASS_COOKIE_NAME });
    const cookieMvpd = _.get(previewPassCookie, 'mvpd');
    const cookieAnalyticsStarted = _.get(previewPassCookie, TrackEvents.PREVIEW_PASS_STARTED);
    const isCookieProviderCurrent = cookieMvpd === mvpdId;
    if (!isCookieProviderCurrent && !cookieAnalyticsStarted) {
      const newPpCookie = {
        name: PREVIEW_PASS_COOKIE_NAME,
        value: {
          ...previewPassCookie,
          [TrackEvents.PREVIEW_PASS_STARTED]: true,
        },
        options: { path: '/' },
      };
      setCookie(newPpCookie);
      const profileId = _.get(account, 'profileId');
      const previewPassEvents = getPreviewPassAnalyticsEvents({
        previewPassLength: getPreviewPassLengthFromName(mvpdId),
        profileId,
      });
      const onPreviewPassStarted = _.get(
        previewPassEvents,
        TrackEvents.PREVIEW_PASS_STARTED,
        _.noop,
      );
      onPreviewPassStarted();
    }
  }
  return providerObject;
}

export function getDefaultAuthProps({ account, previewPassExpired, previewPassSignIn }) {
  const {
    pcs_contentRestrictedTitle: ratingHeader,
    pcs_contentRestrictedMessage: ratingBody,
    auth_noEntitlements: contentHeader,
    auth_limitedAccessMessage: authLimitedAccessMessage,
    auth_failedMessage: authFailedMessage,
    error_generic: genericError,
    nfl_restrictedMessage_subText: noSportsOnMobileErrorHeader,
    nfl_restrictedMessage_detailText: noSportsOnMobileErrorBody,
  } = window.Language;
  const { provider } = this.props;
  let mvpdBlackoutMessage;

  if (provider) {
    mvpdBlackoutMessage = `This program is no longer available. Please contact your TV provider, ${provider.name}, for more information.`;
  }

  const authZErrorMapping = {
    403: {
      1000: { header: authLimitedAccessMessage, body: '' },
      1002: { header: ratingBody, body: '' },
      1003: { header: ratingBody, body: '' },
      // 1004: {}, TODO: Update once this is added to config
      1005: { header: authFailedMessage, body: '' },
      1007: { header: mvpdBlackoutMessage, body: '' },
    },
    default: { header: genericError, body: '' },
  };

  const {
    auth: { networks },
  } = window.ExternalConfig;
  const additionalAuthorizedNetworks = getAdditionalAuthorizedNetworks();
  const onAuthFailure = ({ err }) => {
    const profileId = _.get(account, 'profileId');
    const action = 'authentication_incomplete';
    const authStartSource = getTealiumSourceAuth() || LOCKED_CONTENT_SOURCE;
    setTealiumSourceAuth(null);
    setTealiumProfileStartSource(null);
    const data = setTealiumProfileId(profileId, {
      action,
      auth_start_source: authStartSource,
      dcg_profile_id: profileId,
      error_message: err,
      mvpd: 'no mvpd',
      network_entitlement_list: '',
      profile_start_source: undefined,
      sso_permission: 'not allowed',
      platform_version: BUILD_VERSION,
      ...getCommonTealiumProps(),
    });
    callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
    optimizelyUserAttributeEvents().authState(ATTEMPTED_AUTH);
  };

  const onAuthSuccess = ({ provider, entitlements = null, providerClick = false }) => {
    const profileId = _.get(account, 'profileId');
    const action = 'authentication_complete';
    const onboardSrc = checkAuthStartSource(previewPassSignIn, previewPassExpired);
    const authStartSource = getTealiumSourceAuth() || LOCKED_CONTENT_SOURCE;
    const networkEntitlements = !_.isEmpty(entitlements) ? entitlements.sort().join() : 'foxnation';
    const mvpd = _.get(provider, 'MVPD', 'no mvpd');
    const onboardingStartSource = onboardSrc;
    setTealiumSourceAuth(null);
    setTealiumProfileStartSource(null);
    const data = setTealiumProfileId(profileId, {
      action,
      auth_start_source: authStartSource,
      authentication_complete_type: 'direct authentication',
      dcg_profile_id: profileId,
      mvpd,
      network_entitlement_list: networkEntitlements,
      onboarding_action: TealiumOnboardingActions.COMPLETE,
      onboarding_start_source: onboardingStartSource,
      platform_version: BUILD_VERSION,
      profile_start_source: undefined,
      sso_permission: 'not allowed',
      ...getCommonTealiumProps(),
    });

    if (isPreviewPass(mvpd)) {
      const previewPassLength = mvpd === PREVIEW_PASS_60 ? 60 : 5;
      getSegmentMVPDEvents().previewPassStarted({
        page_preview_pass_length: previewPassLength,
      });
    }

    optimizelyUserAttributeEvents().authState(SUCCESS_AUTH);
    if (providerClick) {
      // Only fire events upon original authentication
      callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
      getSegmentAuthTrackEvents().authSuccess();
    }
  };

  return {
    onAuthFailure,
    onAuthSuccess,
    contentNoAuthZText: {
      header: contentHeader,
      body: '',
    },
    ratingNoAuthZText: { header: ratingHeader, body: ratingBody },
    noSportsText: { header: noSportsOnMobileErrorHeader, body: noSportsOnMobileErrorBody },
    additionalAuthorizedNetworks,
    authNetworks: networks,
    authZErrorMapping,
  };
}

export function getMVPDUpsellRedirectingProps({ sharedTheme, theme, onOverlayClose }) {
  const { preauthorizedResources } = this.props;
  const {
    auth_redirectHeader: headerText,
    auth_redirectMessage: messageText,
    auth_redirectCTA: buttonText,
  } = window.Language;

  const headerBarTag = ({ onClose }) => (
    <div key="header-bar" className={sharedTheme.headerBarExtraPadding}>
      <div className={sharedTheme.headerContainer}>
        <div key="logo" className={sharedTheme.headerLogo}>
          <NetworkLink
            theme={sharedTheme}
            alt="Fox Nation logo"
            clickEvent={() => {
              onClose();
            }}
            preauthorizedResources={preauthorizedResources}
          />
        </div>
        <button
          aria-label="close"
          key="close"
          onClick={() => {
            onClose();
          }}
          className={sharedTheme.close}
          type="button"
        />
      </div>
    </div>
  );

  headerBarTag.propTypes = {
    onClose: PropTypes.func.isRequired,
  };

  const headerTag = ({ onConfirm }) => (
    <div key="header-text" className={classNames(sharedTheme.content, theme.content)}>
      <div className={classNames(sharedTheme.headerText, theme.headerText)}>
        {headerText}
        <div className={classNames(sharedTheme.descriptionText, theme.descriptionText)}>
          {messageText}
        </div>
        <div
          key="confirm-button"
          className={classNames(sharedTheme.confirmContainer, theme.confirmContainer)}
        >
          <button
            onClick={onConfirm}
            className={classNames(sharedTheme.confirm, theme.confirm)}
            type="button"
          >
            <span>{buttonText}</span>
          </button>
        </div>
      </div>
    </div>
  );

  const redirectingOnClose = () => {
    if (onOverlayClose) onOverlayClose();
  };

  const innerContainerChildren = ({ onClose, onConfirm }) => [
    headerBarTag({ onClose }),
    headerTag({ onConfirm }),
  ];

  return {
    redirectingBackgroundClass: 'withBackground',
    redirectingInnerContainerChildren: innerContainerChildren,
    redirectingOnClose,
    redirectingTheme: theme,
  };
}

export async function getDesignatedMarketingAreaData(options) {
  return new Promise(async (resolve, reject) => {
    try {
      const { deviceId, zipCode } = options;
      if (!zipCode) throw new Error('zip code must be provided');
      const endpointPathKey = 'dmaService';
      const route = helperGetApiRouteById(endpointPathKey, zipCode, '{zipCode}');
      const headers = helperGetApiHeaders({ pathKey: endpointPathKey, deviceId });
      const designatedMarketingAreaResponse = await axios
        .get(route, headers)
        .catch(e => apiErrorHandler({ error: e }));
      const designatedMarketingAreaData = _.get(designatedMarketingAreaResponse, 'data.member', []);
      resolve(designatedMarketingAreaData);
    } catch (e) {
      reject(e);
    }
  });
}

export const getPreviewPassTime = () => {
  if (!previewPassEnabled()) return 0;
  const cookie = getMVPDCookie();
  return _.get(cookie, 'authn_expire', 0);
};

export const getMVPDExpireTime = () => {
  const cookie = getMVPDCookie();
  return _.get(cookie, 'authn_expire', 0);
};

export function getMvpdDirectSignInRedirectProps({ onOverlayClose, sharedTheme, theme }) {
  const { preauthorizedResources } = this.props;
  const mvpdDirectSignInChildren = ({ onUpgradeClick }) => {
    const {
      auth_mvpdRedirctHeader: headerText,
      auth_mvpdRedirctMessage: descriptionText,
      auth_mvpdRedirctCTA: primaryButtonText,
      auth_mvpdRedirctSignIn: secondaryButtonText,
    } = window.Language;

    const iconTag = (
      <div key="header-icon">
        <Icon color="white" icon="checkmark-circle" theme={theme} viewBox="0 0 71 71" />
      </div>
    );

    const headerTextTag = (
      <div key="header-text" className={classNames(sharedTheme.headerText, theme.headerText)}>
        {headerText}
      </div>
    );

    const descriptionTextTag = (
      <div
        key="description-text"
        className={classNames(sharedTheme.descriptionText, theme.descriptionText)}
      >
        {helperMarkdownToComponent(descriptionText)}
      </div>
    );

    const actionButtonTag = (
      <div
        key="redirect-buttons"
        className={classNames(sharedTheme.confirmContainer, theme.buttonContainer)}
      >
        <button
          onClick={onUpgradeClick}
          className={classNames(sharedTheme.confirm, theme.primaryButton)}
          type="button"
        >
          {primaryButtonText}
        </button>
        <button
          onClick={goToExternalSignIn}
          className={classNames(sharedTheme.confirm, theme.secondaryButton)}
          type="button"
        >
          {secondaryButtonText}
        </button>
      </div>
    );

    return [iconTag, headerTextTag, descriptionTextTag, actionButtonTag];
  };

  const mvpdDirectSignInInnerContainerChildren = () => {
    const headerBarTag = (
      <div key="header-bar" className={sharedTheme.headerBarExtraPadding}>
        <div className={sharedTheme.headerContainer}>
          <div key="logo" className={sharedTheme.headerLogo}>
            <NetworkLink
              theme={sharedTheme}
              alt="Fox Nation logo"
              clickEvent={() => {
                if (onOverlayClose) onOverlayClose();
              }}
              preauthorizedResources={preauthorizedResources}
            />
          </div>
        </div>
      </div>
    );
    return [headerBarTag];
  };
  return {
    mvpdDirectSignInTheme: theme,
    mvpdDirectSignInChildren,
    mvpdDirectSignInBackgroundClass: 'withBackground',
    mvpdDirectSignInInnerContainerChildren,
  };
}

export async function previewPassLogout(props) {
  const { setAuthorizationToken, mvpdLogout } = props || this.props;

  const mvpdCookie = getMVPDCookie();
  const isMvpdExpired = isTimestampExpired(mvpdCookie?.authn_expire);
  let response;
  if (mvpdCookie?.accessToken && !isMvpdExpired) {
    const { success, account } = await mvpdLogout();
    if (success) {
      response = account;
      const { accessToken: logoutAccessToken } = account;
      removeMVPDCookie();
      setAuthorizationToken(logoutAccessToken);
    }
  }

  return response;
}

export async function authMVPDLogout() {
  const { setAuthorizationToken, mvpdLogout } = this.props;

  try {
    const logoutResponse = await mvpdLogout();
    if (logoutResponse) {
      const { accessToken: logoutAccessToken } = logoutResponse;
      const MVPDEvents = getSegmentMVPDEvents();
      MVPDEvents.authenticationLoggedOut();
      removeCookie({ key: BUYTHROUGH_DATA_COOKIE_NAME, options: { path: '/' } });
      removeMVPDCookie();
      removeCookie({ key: SUCCESS_COOKIE_NAME, options: { path: '/' } });
      this.setState({ providerSet: false, mvpd: null });
      optimizelyUserAttributeEvents().authState(SIGN_OUT_SUCCESS_AUTH);
      if (logoutAccessToken) {
        setAuthorizationToken(logoutAccessToken);
      }
    }
    redirectToExternalUrl(helperGetHomeUrl());
  } catch (error) {
    optimizelyUserAttributeEvents().authState(SIGN_OUT_ATTEMPTED_AUTH);
  }
}

export const getDmaFromVideo = video => {
  const dma = _.get(video, 'trackingData.context.location.dma', null);
  return dma === '999' ? null : dma;
};

export const isGoogleHoldAccount = subscription =>
  subscription?.status === ON_HOLD_SUBSCRIPTION &&
  subscription?.paymentMethod === GOOGLE_PAYMENT_METHOD;

export const isRokuHoldAccount = subscription =>
  (subscription?.status === ON_HOLD_ROKU_SUBSCRIPTION ||
    subscription?.status === ON_HOLD_SUBSCRIPTION) &&
  subscription?.paymentMethod === ROKU_PAYMENT_METHOD;

export const parseJwt = token => {
  const base64Url = token?.split('.')[1];
  const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/');
  if (!base64) return null;
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

export const getSessionId = token => {
  const parsedJSON = parseJwt(token);
  return parsedJSON?.sid;
};

export const stripAuthProperties = authData => {
  const strippedAuthData = { ...authData };
  delete strippedAuthData.brand;
  delete strippedAuthData.encryptedProfileId;
  delete strippedAuthData.hasEmail;
  delete strippedAuthData.hasSocialLogin;
  delete strippedAuthData.idToken;
  delete strippedAuthData.isVerified;
  delete strippedAuthData.linkedMVPD;
  delete strippedAuthData.newsLetter;
  delete strippedAuthData.scope;
  delete strippedAuthData.tokenType;
  delete strippedAuthData.unlinkedMVPD;
  return strippedAuthData;
};
